import React, { useState, useEffect, useCallback } from 'react'
import { Dialog, Button } from 'components/ui'
import {
    HiOutlineSparkles,
    HiOutlineCalendar,
    HiOutlineDocumentReport,
    HiOutlineMicrophone,
    HiOutlineShieldCheck,
    HiOutlineCheck,
    HiOutlineArrowRight,
    HiX,
} from 'react-icons/hi'
import { useNavigate } from 'react-router-dom'
import { useTour } from 'utils/tours/TourProvider'
import { NOVEDADES_VERSION, NOVEDADES_DATA } from 'configs/novedades.config'

const iconMap = {
    calendar: HiOutlineCalendar,
    document: HiOutlineDocumentReport,
    microphone: HiOutlineMicrophone,
    shield: HiOutlineShieldCheck,
}

export default function ModalNovedades() {
    const [isOpen, setIsOpen] = useState(false)
    const { listo, hasSeenTour, markTourSeen } = useTour()
    const navigate = useNavigate()

    // Comprobar si el usuario ya vio esta versión
    const comprobarSiVisto = useCallback(() => {
        const vistoEnTour = hasSeenTour(NOVEDADES_VERSION)
        const vistoEnLocal = localStorage.getItem(`vitaliacare_novedades_${NOVEDADES_VERSION}`) === 'true'
        return vistoEnTour || vistoEnLocal
    }, [hasSeenTour])

    // Apertura automática si hay novedades no vistas
    useEffect(() => {
        if (!listo) return

        const yaVisto = comprobarSiVisto()
        if (!yaVisto) {
            const timer = setTimeout(() => {
                setIsOpen(true)
            }, 700)
            return () => clearTimeout(timer)
        }
    }, [listo, comprobarSiVisto])

    // Listener para abrir el modal manualmente desde el menú de usuario o cualquier parte de la app
    useEffect(() => {
        const handleOpen = () => setIsOpen(true)
        window.addEventListener('vitaliacare:open-novedades', handleOpen)
        return () => window.removeEventListener('vitaliacare:open-novedades', handleOpen)
    }, [])

    const handleMarcarEnterado = () => {
        // Registrar en backend a través de TourProvider y en localStorage
        markTourSeen(NOVEDADES_VERSION)
        try {
            localStorage.setItem(`vitaliacare_novedades_${NOVEDADES_VERSION}`, 'true')
        } catch (_) {}
        setIsOpen(false)
    }

    const handleIrAFuncion = (ruta) => {
        handleMarcarEnterado()
        if (ruta) {
            navigate(ruta)
        }
    }

    return (
        <Dialog
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
            onRequestClose={() => setIsOpen(false)}
            width={680}
            closable={false}
            contentClassName="!p-0 !rounded-2xl overflow-hidden shadow-2xl border-0"
        >
            <div className="flex flex-col max-h-[90vh] sm:max-h-[85vh] overflow-hidden bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 rounded-2xl">
                {/* Encabezado con degradado y destellos */}
                <div className="relative shrink-0 bg-gradient-to-r from-teal-800 via-vitalia-700 to-indigo-900 p-6 sm:p-7 text-white select-none">
                    <button
                        type="button"
                        onClick={() => setIsOpen(false)}
                        className="absolute top-4 right-4 z-10 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-2 transition-all flex items-center justify-center cursor-pointer shadow-sm"
                        title="Cerrar ventana"
                    >
                        <HiX className="text-lg" />
                    </button>

                    {/* Efectos de fondo difuminados */}
                    <div className="pointer-events-none absolute -top-12 -right-12 h-44 w-44 rounded-full bg-teal-400/20 blur-3xl" />
                    <div className="pointer-events-none absolute -bottom-8 -left-8 h-36 w-36 rounded-full bg-indigo-500/20 blur-2xl" />

                    <div className="relative z-10 flex items-start gap-3.5">
                        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/20 text-white text-2xl backdrop-blur-md shadow-inner border border-white/20">
                            <HiOutlineSparkles className="animate-pulse" />
                        </span>
                        <div>
                            <div className="flex items-center gap-2 mb-1">
                                <span className="inline-flex items-center text-[10px] font-extrabold uppercase tracking-widest bg-emerald-400/25 border border-emerald-300/40 text-emerald-200 px-2.5 py-0.5 rounded-full backdrop-blur-sm">
                                    {NOVEDADES_DATA.tag}
                                </span>
                            </div>
                            <h3 className="text-xl sm:text-2xl font-black text-white tracking-tight leading-snug">
                                {NOVEDADES_DATA.titulo}
                            </h3>
                            <p className="text-xs sm:text-sm text-teal-100/90 leading-relaxed mt-1 max-w-xl font-medium">
                                {NOVEDADES_DATA.subtitulo}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Lista de novedades scrolleable */}
                <div className="flex-1 overflow-y-auto p-5 sm:p-6 space-y-3.5 divide-y divide-gray-100 dark:divide-gray-800/80">
                    {NOVEDADES_DATA.items.map((item, idx) => {
                        const IconComponent = iconMap[item.tipo] || HiOutlineSparkles
                        return (
                            <div
                                key={item.id}
                                className={`flex flex-col sm:flex-row items-start gap-4 p-3.5 sm:p-4 rounded-xl transition-all hover:bg-gray-50/90 dark:hover:bg-gray-800/50 border border-transparent hover:border-gray-200/80 dark:hover:border-gray-700/60 ${
                                    idx > 0 ? 'pt-4' : ''
                                }`}
                            >
                                <div
                                    className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border text-2xl shadow-sm ${item.iconoBg}`}
                                >
                                    <IconComponent />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex flex-wrap items-center gap-2 mb-1">
                                        <h4 className="text-sm sm:text-base font-bold text-gray-900 dark:text-gray-100">
                                            {item.titulo}
                                        </h4>
                                        <span
                                            className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${item.badgeClass}`}
                                        >
                                            {item.badge}
                                        </span>
                                    </div>
                                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                                        {item.descripcion}
                                    </p>
                                    {item.ruta && (
                                        <button
                                            type="button"
                                            onClick={() => handleIrAFuncion(item.ruta)}
                                            className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-vitalia-600 dark:text-vitalia-400 hover:text-vitalia-700 dark:hover:text-vitalia-300 hover:underline cursor-pointer group"
                                        >
                                            <span>{item.textoAccion || 'Probar funcionalidad'}</span>
                                            <HiOutlineArrowRight className="text-xs transition-transform group-hover:translate-x-0.5" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        )
                    })}
                </div>

                {/* Pie de modal con acción principal */}
                <div className="shrink-0 flex flex-col sm:flex-row items-center justify-between gap-3 p-4 sm:p-5 bg-gray-50 dark:bg-gray-850 border-t border-gray-200 dark:border-gray-800">
                    <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                        <span className="h-2 w-2 rounded-full bg-emerald-500 animate-ping" />
                        <span>VitaliaCare siempre actualizado para ti</span>
                    </div>

                    <div className="flex items-center gap-2 w-full sm:w-auto">
                        <Button
                            variant="solid"
                            size="md"
                            icon={<HiOutlineCheck className="text-lg" />}
                            onClick={handleMarcarEnterado}
                            className="w-full sm:w-auto !bg-vitalia-600 hover:!bg-vitalia-700 !text-white !font-bold !shadow-md hover:!shadow-lg transition-all"
                        >
                            Marcar como enterado
                        </Button>
                    </div>
                </div>
            </div>
        </Dialog>
    )
}
